import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { ComplexCommission, ComplexCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { mergeErrorsMatrixValidation, mergeErrorsBonusesValidation } from "@/Common/helpers/PayPlans/validations";

// Layouts
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Tiers Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// MUI
import { Grid, Stack, Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const minimumHoursInput = 744;
const maximumHoursInput = 0;

const minimumFlaggedHoursInput = 0;
const maximumFlaggedHoursInput = 9999999;

const updateValueColumn = (args) => {
	const { currentTiers, column, updatedValue, area } = args;

	currentTiers.forEach((tier) => {
		tier.commissions?.forEach((commission, key) => {
			if (area in commission && key === column) {
				commission[area]["value"] = updatedValue;
				if (tier.commissions?.[key + 1]) {
					tier.commissions[key + 1]["from"]["value"] = updatedValue;
				}
			}
		});
	});
};

const updateValueRow = (args) => {
	const { currentTiers, row, column, updatedValue, area } = args;

	if (area !== "commission") {
		currentTiers[row][area]["value"] = updatedValue;
		if (currentTiers?.[row + 1] && area === "to") {
			currentTiers[row + 1]["from"]["value"] = updatedValue + 0.01;
		}
	} else {
		currentTiers[row]["commissions"][column][area]["value"] = updatedValue;
	}
};

const getUpdatableTier = (tier) => {
	const { from, to, commissions = [] } = tier || {};
	return {
		from,
		to,
		commissions,
	};
};

const mapTiersToUpdate = (tiers = []) => tiers.map((tier) => getUpdatableTier(tier));

export default function FlaggedHours(props) {
	const {
		sectionId = "flaggedHoursBonus",
		content = {
			tiers: [],
			applyTiersRetroactively: false,
		},
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { errors: modelErrors, addSectionError, removeSectionError, getErrorKey } = useSectionsErrors?.() || {};
	const { errors, isValid, getSectionsErrorMessages, validate } = useValidations();

	const localErrorsMessages = getSectionsErrorMessages(["hours", "flaggedHours", "commissions", "matrix"]);
	const headerErrors = errors?.get("hours");
	const rowsErrors = errors?.get("flaggedHours");
	const columnsErrors = errors?.get("commissions");
	const matrixErrors = errors?.get("matrix");

	// Control to send update actions
	const wasUpdated = useRef(false);

	const updateModel = (args) => {
		const { updatedTiers, updatedApplyTiersRetroactively } = args;

		isValid ? removeSectionError(sectionId) : addSectionError(sectionId);
		const currentErrors = modelErrors?.get(sectionId) || [];
		const filteredErrors = currentErrors.filter((error) => error !== getErrorKey(sectionId)) || [];
		const validToSave = isValid && wasUpdated.current && !filteredErrors.length;

		if (validToSave) {
			const modelToPropagate = {
				tiers: mapTiersToUpdate(updatedTiers),
				applyTiersRetroactively: updatedApplyTiersRetroactively,
			};
			onActionUpdate?.(sectionId, null, modelToPropagate, isValid);
		}
	};

	const onTiersUpdate = (updatedTiers) => {
		isValid ? removeSectionError(sectionId) : addSectionError(sectionId);
		updateModel({
			updatedTiers,
			updatedApplyTiersRetroactively: applyTiersRetroactively,
		});
	};

	const onSwitchUpdate = (updatedApplyTiersRetroactively) => {
		updateModel({
			updatedTiers: innerTiers,
			updatedApplyTiersRetroactively,
		});
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);
	const [applyTiersRetroactively, setApplyTiersRetroactively] = useCallbackState(false, onSwitchUpdate);

	// Methods
	const handleAddColumn = () => {
		const currentTiers = [...innerTiers];
		const commissionInput = currentTiers?.[0]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Percent";
		const lastIndex = currentTiers?.[0]?.["commissions"]?.length ? currentTiers[0]["commissions"].length - 1 : 0;

		currentTiers.forEach((tier) => {
			tier.commissions.push(
				ComplexCommission([minimumHoursInput, "Duration"], [0, "Duration"], {
					identifier: "commission",
					commission: [0, commissionInput],
				})
			);
			if (tier?.commissions?.[lastIndex]) {
				tier.commissions[lastIndex]["to"]["value"] = minimumHoursInput;
			}
		});

		update(currentTiers);
	};

	const handleDeleteColumn = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers?.[0]?.["commissions"]?.length > minimumColumns) {
			currentTiers.forEach((tier) => {
				tier.commissions.splice(-1);
				const lastIndex = tier?.commissions?.length ? tier.commissions.length - 1 : 0;
				tier.commissions[lastIndex]["to"]["value"] = maximumHoursInput;
			});
		}

		update(currentTiers, true);
	};

	const handleAddTier = () => {
		const currentTiers = [...innerTiers];
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		const lastInput = currentTiers?.[lastIndex]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Unit";
		const previousCommissions = currentTiers?.[lastIndex]?.["commissions"] || [];

		const commissions = previousCommissions.map((commission) => {
			const previousFrom = commission?.["from"]?.["value"] || 0;
			const previousTo = commission?.["to"]?.["value"] || 0;
			return ComplexCommission([previousFrom, "Duration"], [previousTo, "Duration"], {
				identifier: "commission",
				commission: [0, lastInput],
			});
		});

		currentTiers.push(ComplexCommissionTier([minimumFlaggedHoursInput, "Unit"], [maximumFlaggedHoursInput, "Unit"], { commissions }));

		if (currentTiers?.length) {
			currentTiers[lastIndex].to.value = minimumFlaggedHoursInput;
		}

		update(currentTiers);
	};

	const handleDeleteTier = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers.length >= 1) {
			currentTiers.splice(-1);
		}
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		currentTiers[lastIndex].to.value = maximumFlaggedHoursInput;

		update(currentTiers, true);
	};

	const update = (updatedTiers = [], wasUpdatedValue = false) => {
		wasUpdated.current = wasUpdatedValue;
		setInnerTiers(clone(updatedTiers));
	};

	const handleUpdateValue = (...params) => {
		const [row, column, location, area, value] = params;
		const currentTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updateValueColumn({ currentTiers, column, updatedValue, area });
				break;
			case "row":
				updateValueRow({ currentTiers, row, column, updatedValue, area });
				break;
			default:
				break;
		}

		update(currentTiers, true);
	};

	const handleUpdateInput = (...params) => {
		const [, , location, area, input] = params;
		const currentTiers = [...innerTiers];
		switch (location) {
			case "column":
				break;
			case "row":
				currentTiers.forEach((tier) => {
					tier.commissions?.forEach((commission) => {
						if (area in commission) {
							commission[area]["input"] = input;
							commission[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(currentTiers, true);
	};

	const handleUpdateSwitch = (value) => {
		wasUpdated.current = true;
		setApplyTiersRetroactively(value);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "hours",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Units invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.commissions || [],
				},
				{
					definition: {
						useKey: "flaggedHours",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<",
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0.01 },
									distinct: 1,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=",
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsBonusesValidation,
							},
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect commissions definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "matrix",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=",
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsMatrixValidation,
							},
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Please add at least a two tiers configuration",
						validationType: "COMPOSED",
					},
					section: innerTiers,
				},
			]);
		}
	}, [innerTiers, validate]);

	useEffect(() => {
		if (content) {
			const { tiers, applyTiersRetroactively: applyRetroactively } = content;
			setInnerTiers(clone(tiers));
			setApplyTiersRetroactively(applyRetroactively);
		}
		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [content, setInnerTiers]);

	if (loading) {
		return (
			<Stack direction="row" spacing={2} margin={5} mb={1}>
				<DefaultTiersLoader mt={1} buttons />
			</Stack>
		);
	}

	return (
		<Grid container item mt={1} xs={12}>
			<Stack direction={"row"} justifyContent={"flex-end"} flex={1} mr={5} pb={1}>
				<CustomizedSwitches
					key={"applyTiersRetroactively"}
					textLeft={true}
					textChecked={"Apply retroactively"}
					textUnChecked={"Apply retroactively"}
					checked={applyTiersRetroactively}
					disabled={readOnly}
					onChange={handleUpdateSwitch}
				/>
			</Stack>
			<Grid item xs={12}>
				<TierColumns
					titleLabel={"Clock Hours"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "241px", width: "241px", marginRight: "3px" },
					}}
					titleProps={{
						className: "tiered-title tiered-title-adjustment",
						sx: null,
					}}
					disabledDelete={innerTiers?.[0]?.commissions?.length <= minimumColumns || readOnly}
					disabledAdd={innerTiers?.[0]?.commissions?.length >= maximumColumns || readOnly}
					onClickDelete={handleDeleteColumn}
					onClickAdd={handleAddColumn}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerTiers?.[0]?.commissions?.map((tier, key) => {
							const indexKey = `flagged_hours_bonus_column_${key}`;
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerTiers?.[0]?.commissions.length === key + 1);

							return (
								<TierColumnsItem key={indexKey} index={key} title={"Comm."}>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>
			</Grid>
			<Grid item xs={12} mt={1} ml={2} minHeight={"100px"}>
				<TierRows
					showTitle
					title={"Flagged Hours"}
					sxTitlePropsContainer={{
						top: 102,
					}}
					centerTitleAfterRow={2}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
					addButtonDisabled={innerTiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 170,
						totalColumns: innerTiers?.[0]?.commissions?.length || 1,
						totalRows: innerTiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={handleDeleteTier}
					onClickAdd={handleAddTier}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerTiers?.map((tier, key) => {
									const indexKey = `flagged_hours_bonus_row_${key}`;
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerTiers?.length === key + 1);
									const commissionsLength = innerTiers?.[0]?.commissions?.length || 0;
									const reduction = commissionsLength * 5 + 70 + commissionsLength;

									return (
										<Grid key={indexKey} container item ml={2}>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={fromDisabled}
												toDisabled={toDisabled}
												spaces={{
													mainSection: 235,
													columns: 170,
													totalColumns: innerTiers?.[0]?.commissions?.length || 1,
													totalRows: innerTiers?.length || 1,
													separation: 8.5,
													reduction,
												}}
												rowsErrors={rowsErrors || []}
												columnsErrors={columnsErrors?.length ? columnsErrors : matrixErrors || []}
												readOnly={readOnly}
												spacing={"80px"}
												sxMainContent={{ minWidth: "170.41px", marginRight: "3px" }}
												sxCells={{ minWidth: "100.42px" }}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} ml={2} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => {
								const indexKey = `error_flagged_hours_${key}`;
								return <TierAlertError key={indexKey} text={message} />;
							})}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
FlaggedHours.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		tiers: PropTypes.array,
		applyTiersRetroactively: PropTypes.bool,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
